import { clsx } from "clsx";
import currency from "currency.js";
import { customAlphabet } from "nanoid";
import { twMerge } from "tailwind-merge";
import config from "@/config";
const nanoid = customAlphabet("1234567890abcdef", 10);

export const getNanoId = () => {
  return nanoid();
};

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const scrollRefToBottom = (ref) => {
  if (ref.current) {
    ref.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }
};

export const getFormattedDate = (date) => {
  const format = { month: "long", day: "numeric" };
  return new Date(date).toLocaleDateString("en-US", format);
};

export const getFormattedDateNew = (date) => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const d = new Date(date);
  const dayName = days[d.getDay()]; // Get the day of the week
  const day = d.getDate(); // Get the day of the month
  const monthName = months[d.getMonth()]; // Get the month name
  const year = d.getFullYear(); // Get the year

  return `${dayName} ${day} ${monthName} ${year}`;
};

export const getFormattedTime = (date) => {
  // Parse the date string to get a Date object
  const parsedDate = new Date(date);

  // Extract hours and minutes from the Date object
  const hours = parsedDate.getHours();
  const minutes = parsedDate.getMinutes();

  // Format the hours and minutes as HH:MM
  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;

  return formattedTime;
};

export const cloneDeep = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * Resolves a value from a locale-keyed object for the exact locale, or any
 * sibling region that shares the same base language (e.g. "de-AT" → "de-DE",
 * "nl-BE" → "nl-NL"). Returns undefined when nothing matches.
 *
 * This is what lets a user whose locale is "de-AT" still see the German catalog
 * value, which the print.com import stores under "de-DE". Without it, the exact
 * lookup misses and we fall through to English. NOTE: it only bridges *region*
 * differences within a language already present in the object — locales whose
 * base language was never imported (e.g. "it-IT", "da-DK") still have no value
 * to resolve to and will fall back to English.
 * @param {Object} languageObject - The object containing language values.
 * @param {string} desiredLanguage - The desired locale key (e.g. "de-AT").
 * @returns {string|undefined} - The matched value, or undefined.
 */
export function getLanguageValue(languageObject, desiredLanguage) {
  if (
    !languageObject ||
    typeof languageObject !== "object" ||
    !desiredLanguage
  ) {
    return undefined;
  }
  if (languageObject[desiredLanguage]) return languageObject[desiredLanguage];
  const base = desiredLanguage.split("-")[0];
  const siblingKey = Object.keys(languageObject).find(
    (key) => key.split("-")[0] === base && languageObject[key]
  );
  return siblingKey ? languageObject[siblingKey] : undefined;
}

/**
 * Resolves a localized value with graceful fallback:
 * exact locale → same base language (de-AT → de-DE) → English. Returns undefined
 * when nothing matches, so callers can apply their own fallback.
 * @param {Object} languageObject - The object containing language values.
 * @param {string} desiredLanguage - The desired locale key.
 * @returns {string|undefined} - The resolved value, or undefined.
 */
export function resolveLocaleValue(languageObject, desiredLanguage) {
  return (
    getLanguageValue(languageObject, desiredLanguage) ??
    getLanguageValue(languageObject, "en-GB")
  );
}

/**
 * Returns the value for the desired locale, falling back to a sibling region in
 * the same base language (de-AT → de-DE), then English, then the provided fallback.
 * @param {Object} languageObject - The object containing language values.
 * @param {string} desiredLanguage - The desired locale key.
 * @param {string} [fallBack] - Value to use when nothing resolves.
 * @returns {string} - The resolved value or the fallback.
 */
export function getLocalizedValue(languageObject, desiredLanguage, fallBack) {
  if (typeof languageObject !== "object") {
    console.warn("getLocalizedValue should receive an object");
    return fallBack || languageObject;
  }
  return resolveLocaleValue(languageObject, desiredLanguage) ?? fallBack ?? "";
}

export function removeItemAtIndex(array, index) {
  return array.filter((_, i) => i !== index);
}

export function reorderArray(array, fromIndex, toIndex) {
  const result = [...array];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

export function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    clearTimeout(timeout);
    if (immediate && !timeout) func.apply(context, args);
    timeout = setTimeout(function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    }, wait);
  };
}

/**
 * Detects if the current device is a desktop using user agent detection
 * @returns {boolean} - True if the device is a desktop, false if it's mobile
 */
export function isDesktopDevice() {
  // Check if window is defined (client-side)
  if (typeof window === "undefined") return false;

  // Use user agent to detect mobile devices
  const userAgent = window.navigator.userAgent;

  // Check if the user agent contains common mobile device identifiers
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  return !mobileRegex.test(userAgent);
}

export const localeToRegion = {
  "nl-NL": "NL",
  "nl-BE": "BE",
  "en-GB": "GB",
  "fr-FR": "FR",
  "fr-BE": "BE",
  "de-DE": "DE",
  "de-AT": "AT",
  "es-ES": "ES",
  "it-IT": "IT",
  "da-DK": "DK",
};

export function formatPrice(price, locale, currencyCode = "EUR") {
  // GBP always uses dot decimal (£14.70), regardless of locale
  if (currencyCode === "GBP") {
    return (
      currency(price, {
        symbol: "£",
        decimal: ".",
        separator: ",",
        precision: 2,
      }).format() + ""
    );
  }

  if (!locale) {
    return (
      currency(price, {
        symbol: "€",
        decimal: ",",
        separator: ".",
        precision: 2,
      }).format() + ""
    );
  }

  switch (locale) {
    case "nl-NL":
    case "nl-BE":
      return (
        currency(price, {
          symbol: "€ ",
          decimal: ",",
          separator: ".",
          precision: 2,
        }).format() + ""
      );

    case "en-GB":
      return (
        currency(price, {
          symbol: "€",
          decimal: ".",
          separator: ",",
          precision: 2,
        }).format() + ""
      );

    case "fr-BE":
    case "fr-FR":
      return (
        currency(price, {
          symbol: " €",
          decimal: ",",
          separator: ".",
          precision: 2,
          pattern: "# !",
        }).format() + ""
      );

    case "de-DE":
    case "de-AT":
    case "es-ES":
      return (
        currency(price, {
          symbol: " €",
          decimal: ",",
          separator: ".",
          precision: 2,
          pattern: "# !",
        }).format() + ""
      );

    default:
      return (
        currency(price, {
          symbol: "€",
          decimal: ",",
          separator: ".",
          precision: 2,
        }).format() + ""
      );
  }
}

/**
 * Flattens a nested object into a single-level object with dot-notation keys.
 * @param {Object} obj - The object to flatten.
 * @param {string} [prefix=''] - The prefix to use for the keys (used for recursion).
 * @returns {Object} - The flattened object.
 */
export function flattenObject(obj, prefix = "") {
  return Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? `${prefix}.` : "";
    if (
      typeof obj[k] === "object" &&
      obj[k] !== null &&
      !Array.isArray(obj[k])
    ) {
      Object.assign(acc, flattenObject(obj[k], `${pre}${k}`));
    } else {
      acc[`${pre}${k}`] = obj[k];
    }
    return acc;
  }, {});
}

// Function to extract text from HTML and truncate
export const truncateHtml = (html, maxLength) => {
  // DOMParser creates an inert document: parsed event handlers and active
  // elements are never attached to the live page. Do not use a temporary live
  // element + innerHTML here; reseller-controlled descriptions reach this
  // helper on the public widget.
  const parsed = new DOMParser().parseFromString(
    String(html ?? ""),
    "text/html"
  );
  const text = parsed.body.textContent || "";

  // Truncate the text
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

/**
 * Unflattens a single-level object with dot-notation keys into a nested object.
 * @param {Object} obj - The object to unflatten.
 * @returns {Object} - The unflattened object.
 */
export function unflattenObject(obj) {
  const result = {};

  for (const key in obj) {
    const keys = key.split(".");
    let current = result;

    for (let i = 0; i < keys.length; i++) {
      if (i === keys.length - 1) {
        current[keys[i]] = obj[key];
      } else {
        current[keys[i]] = current[keys[i]] || {};
        current = current[keys[i]];
      }
    }
  }

  return result;
}

/**
 * Converts a locale string to its corresponding language code
 * @param {string} locale - The locale string (e.g., 'en-US', 'nl-NL')
 * @returns {string} - The language code (e.g., 'en', 'nl')
 */
export function localeToLanguage(locale) {
  if (!locale) return "en"; // Default fallback
  return locale.split("-")[0].toLowerCase();
}

/**
 * Converts a locale string to its corresponding language label
 * @param {string} locale - The locale string (e.g., 'en-US', 'nl-NL')
 * @returns {string} - The formatted label (e.g., 'English (United States)', 'Dutch (Netherlands)')
 */
export function localeToLabel(locale) {
  if (!locale) return "English"; // Default fallback

  try {
    // Create a new Intl.DisplayNames instance for language and region names
    const languageNames = new Intl.DisplayNames(["en"], { type: "language" });
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

    // Split locale into language and region
    const [lang, region] = locale.split("-");

    // Get the language and region names
    const languageName = languageNames.of(lang);
    const regionName = region ? regionNames.of(region) : null;

    // Return formatted string
    return regionName ? `${languageName} (${regionName})` : languageName;
  } catch (error) {
    console.warn(`Error formatting locale ${locale}:`, error);
    return locale;
  }
}

/**
 * Extracts the country code from a locale string
 * @param {string} locale - The locale string (e.g., 'en-GB', 'nl-NL')
 * @param {string} [defaultCountry='GB'] - The default country code to return if no country is found
 * @returns {string} - The country code (e.g., 'GB', 'NL')
 */
export function localeToCountry(locale, defaultCountry = "GB") {
  if (!locale) return defaultCountry;

  const parts = locale.split("-");
  if (parts.length < 2) return defaultCountry;

  const country = parts[1].toUpperCase();
  return country;
}

/**
 * Maps a country code to its international phone code
 * @param {string} countryCode - The two-letter country code (e.g., 'NL', 'DE')
 * @returns {string} - The international phone code with plus prefix (e.g., '+31', '+49')
 */
export function countryToCountryPhoneCode(countryCode) {
  const phoneCodeMap = {
    // EU Member States
    AT: "+43", // Austria
    BE: "+32", // Belgium
    BG: "+359", // Bulgaria
    HR: "+385", // Croatia
    CY: "+357", // Cyprus
    CZ: "+420", // Czech Republic
    DK: "+45", // Denmark
    EE: "+372", // Estonia
    FI: "+358", // Finland
    FR: "+33", // France
    DE: "+49", // Germany
    GR: "+30", // Greece
    HU: "+36", // Hungary
    IE: "+353", // Ireland
    IT: "+39", // Italy
    LV: "+371", // Latvia
    LT: "+370", // Lithuania
    LU: "+352", // Luxembourg
    MT: "+356", // Malta
    NL: "+31", // Netherlands
    PL: "+48", // Poland
    PT: "+351", // Portugal
    RO: "+40", // Romania
    SK: "+421", // Slovakia
    SI: "+386", // Slovenia
    ES: "+34", // Spain
    SE: "+46", // Sweden
    // Non-EU but important European countries
    GB: "+44", // United Kingdom
    CH: "+41", // Switzerland
    NO: "+47", // Norway
    // Other major countries
    US: "+1", // United States
    CA: "+1", // Canada
    AU: "+61", // Australia
    NZ: "+64", // New Zealand
  };

  const code = countryCode?.toUpperCase();
  return phoneCodeMap[code] || null;
}

/**
 * Converts a language code to its preferred locale
 * @param {string} language - The language code (e.g., 'en', 'nl', 'fr')
 * @returns {string} - The preferred locale (e.g., 'en-GB', 'nl-NL', 'fr-FR')
 */
export function languageToPreferredLocale(
  language,
  returnWhenNotFound = false
) {
  // If input is already a locale (contains '-'), return it if it's in the allowed locales
  if (language.includes("-")) {
    const normalizedLocale = language.toLowerCase();
    const matchingLocale = config.locales.find(
      (locale) => locale.toLowerCase() === normalizedLocale
    );
    if (matchingLocale) {
      return matchingLocale;
    }
  }

  // Preferred locale mappings
  const preferredLocales = {
    nl: "nl-NL", // Prefer nl-NL over nl-BE
    fr: "fr-FR", // Prefer fr-FR over fr-BE
    en: "en-GB",
    de: "de-DE",
    es: "es-ES",
  };

  return preferredLocales[language.toLowerCase()] || returnWhenNotFound; // returns false if not in here
}

/**
 * Gets the preferred locale from Accept-Language header values
 * @param {string} acceptLanguageHeader - Raw Accept-Language header value
 * @param {string} defaultLocale - Fallback locale if no match found
 * @returns {string} - Preferred locale (e.g., "en-GB", "nl-NL")
 */
export function getPreferredLocaleFromHeaders(
  acceptLanguageHeader,
  defaultLocale = "nl-NL"
) {
  function cleanAcceptLanguageValue(acceptLanguageValue) {
    return acceptLanguageValue.split(";")[0];
  }
  try {
    if (!acceptLanguageHeader) {
      return defaultLocale;
    }

    const browserLocales = acceptLanguageHeader
      .split(",")
      .map(cleanAcceptLanguageValue)
      .filter(Boolean); // Remove empty strings

    // Find first supported locale from browser preferences
    for (const browserLocale of browserLocales) {
      const locale = languageToPreferredLocale(browserLocale, false);
      if (locale) {
        return locale;
      }
    }

    return defaultLocale;
  } catch (error) {
    console.error("Error parsing Accept-Language header:", error);
    return defaultLocale;
  }
}

/**
 * Unescapes HTML strings with incorrect backslash escaping
 * @param {string} html - The HTML string to unescape
 * @return {string} The unescaped HTML string
 */
export function unescapeHtml(html) {
  if (!html) return "";

  return (
    html
      // Fix incorrectly escaped attributes
      .replace(/=\\(?=[^"'])/g, '="') // Convert =\ to ="
      .replace(/\\(?=\s|>)/g, '"') // Convert \ before space or > to "
      .replace(/\\_/g, "_") // Fix \_blank to _blank
      .replace(/\\(?=https?:)/g, "") // Remove \ before http(s) URLs

      // Handle any remaining standard escapes
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, "\\")
      .replace(/\\'/g, "'")
      .replace(/\\\//g, "/")
  );
}

// Coupon storage utilities
export const COUPON_STORAGE_KEY = "saved_coupon_code";

export function saveCouponToStorage(couponCode) {
  if (!couponCode || typeof window === "undefined") return;

  try {
    const couponData = {
      code: couponCode,
      expires: Date.now() + 24 * 60 * 60 * 1000, // 24 hours from now
    };
    localStorage.setItem(COUPON_STORAGE_KEY, JSON.stringify(couponData));
  } catch (error) {
    console.error("Error saving coupon to localStorage:", error);
  }
}

export function getStoredCoupon() {
  if (typeof window === "undefined") return null;

  try {
    const storedData = localStorage.getItem(COUPON_STORAGE_KEY);
    if (!storedData) return null;

    const couponData = JSON.parse(storedData);

    // Check if coupon has expired
    if (couponData.expires < Date.now()) {
      localStorage.removeItem(COUPON_STORAGE_KEY);
      return null;
    }

    return couponData.code;
  } catch (error) {
    console.error("Error retrieving coupon from localStorage:", error);
    return null;
  }
}

/**
 * Formats a phone number based on the locale, ensuring it has the correct country code
 * @param {string} phoneNumber - The phone number to format
 * @param {string} locale - The locale string (e.g., 'nl-NL', 'en-GB')
 * @returns {string} - The formatted phone number with country code (e.g., '+31612345678')
 */
export function formatPhoneNumberWithCountryCode(
  phoneNumber,
  locale = "en-GB"
) {
  if (!phoneNumber) return phoneNumber;

  // Clean the phone number - remove all non-digit characters except the leading +
  let cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // If it already starts with +, assume it has the country code
  if (cleaned.startsWith("+")) return cleaned;

  // Get the country code from the locale
  const countryCode = localeToCountry(locale);

  // Get the phone code for the country
  const phoneCode = countryToCountryPhoneCode(countryCode);

  if (!phoneCode) return cleaned; // Return as is if no phone code found

  // Remove leading 0 if present (common in European numbers, e.g., 06-123-45678 in Netherlands)
  if (cleaned.startsWith("0")) {
    cleaned = cleaned.substring(1);
  }

  // Combine the phone code and cleaned number
  return `${phoneCode}${cleaned}`;
}

/**
 * Performs a coin flip, returning true or false with roughly 50% probability.
 * @returns {boolean} - True or false.
 */
export function coinFlip() {
  return Math.random() < 0.5;
}

export const isProduction = () =>
  typeof process !== "undefined" &&
  process.env.NEXT_PUBLIC_BRANCH === "production";

/**
 * Converts a hex color string to RGB values
 * @param {string} hex - The hex color string (e.g., '#ffffff', '#fff', 'ffffff')
 * @returns {Object|null} - Object with r, g, b properties or null if invalid
 */
export function hexToRgb(hex) {
  if (!hex) return null;

  // Remove # if present
  hex = hex.replace("#", "");

  // Convert 3-digit hex to 6-digit
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  // Validate hex format
  if (hex.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return { r, g, b };
}

/**
 * Calculates the relative luminance of a color using W3C WCAG formula
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} - Relative luminance (0-1)
 */
export function getRelativeLuminance(r, g, b) {
  // Convert to 0-1 range
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  // Apply luminance formula
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determines if a hex color is considered "light" based on its luminance
 * @param {string} hex - The hex color string
 * @param {number} threshold - Luminance threshold (default: 0.5)
 * @returns {boolean} - True if the color is light, false if dark
 */
export function isLightColor(hex, threshold = 0.5) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false; // Default to dark if invalid hex

  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  return luminance > threshold;
}

/**
 * Returns the appropriate CSS class for text based on background color
 * @param {string} backgroundColor - The hex background color
 * @returns {string} - CSS class name for text color
 */
export function getCampaignTextClass(backgroundColor) {
  if (!backgroundColor) return "campaign-banner-text-light"; // Default fallback

  const isLight = isLightColor(backgroundColor);
  return isLight ? "campaign-banner-text-dark" : "campaign-banner-text-light";
}

/**
 * Generates a social media share URL for the specified platform
 * @param {string} platform - The social media platform ('linkedin', 'twitter', 'facebook')
 * @param {string} url - The URL to share
 * @param {string} [text] - Optional text to include with the share (mainly for Twitter)
 * @returns {string} - The formatted share URL
 */
export function getSocialShareLink(platform, url, text = "") {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = text ? encodeURIComponent(text) : "";

  switch (platform.toLowerCase()) {
    case "linkedin":
      return encodedText
        ? `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedText}`
        : `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}`;

    case "twitter":
    case "x":
      return encodedText
        ? `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`
        : `https://twitter.com/intent/tweet?url=${encodedUrl}`;

    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

    case "whatsapp":
      return encodedText
        ? `https://wa.me/?text=${encodedText}%20${encodedUrl}`
        : `https://wa.me/?text=${encodedUrl}`;

    default:
      console.warn(`Unsupported platform: ${platform}`);
      return url;
  }
}
